import styled from 'styled-components';

// ------------------------------------------------------------------ //
// Modal surfaces on the redesign tokens. One shared panel (ShellPanel,
// composed via components/core/ModalShell) + the pinned cookie card and
// the toast viewport. Breakpoint matches the shell/useWindowListener.
// ------------------------------------------------------------------ //

const SHEET_BREAK = '@media (max-width: 1079px)';

export const ShellPanel = styled.div<{
	$center?: boolean;
	$size?: 'sm' | 'md' | 'lg';
}>`
	position: relative;
	background: var(--panel-2);
	border: 1px solid var(--hair);
	border-radius: var(--r);
	padding: 36px 38px 32px;
	width: 100%;
	max-width: ${({ $size }) =>
		$size === 'sm' ? '460px' : $size === 'lg' ? '100%' : '600px'};
	margin: 0 auto;
	max-height: min(82vh, 760px);
	display: flex;
	flex-direction: column;
	box-shadow: 0 30px 80px rgba(0, 0, 0, 0.3);
	text-align: ${({ $center }) => ($center ? 'center' : 'left')};

	.m-handle {
		display: none;
	}

	.m-icon {
		color: var(--logo);
		margin-bottom: 12px;
		align-self: ${({ $center }) => ($center ? 'center' : 'flex-start')};
	}

	.m-label {
		font-family: var(--fb);
		font-weight: 600;
		font-size: 13px;
		letter-spacing: 2px;
		text-transform: uppercase;
		color: var(--mut);
		margin-bottom: 8px;
	}

	.m-title {
		font-family: var(--fd);
		font-weight: 400;
		text-transform: uppercase;
		font-size: clamp(22px, 2vw, 27px);
		line-height: 1.12;
		color: var(--ink);
		margin: 0 0 10px;
		padding-right: 46px;
	}

	${({ $center }) => ($center ? '.m-title { padding-right: 0; }' : '')}

	.m-body {
		font-family: var(--fb);
		font-size: 16.5px;
		line-height: 1.5;
		color: var(--brown);
		overflow-y: auto;
		min-height: 0;
	}

	.m-actions {
		display: flex;
		gap: 12px;
		justify-content: ${({ $center }) => ($center ? 'center' : 'flex-end')};
		align-items: center;
		flex-wrap: wrap;
		margin-top: 28px;
	}

	/* body-only dialogs (handle → ✕ → body): start the text below the
	   absolute ✕ button so full-width lines can't run underneath it */
	.m-handle + button + .m-body {
		margin-top: 34px;
	}

	${SHEET_BREAK} {
		border-radius: var(--r-card) var(--r-card) 0 0;
		border-bottom: none;
		padding: 14px 20px calc(26px + env(safe-area-inset-bottom));
		max-height: 84dvh;
		box-shadow: 0 -20px 60px rgba(0, 0, 0, 0.25);

		.m-handle {
			display: block;
			flex-shrink: 0;
			width: 44px;
			height: 4px;
			border-radius: var(--r-pill);
			background: rgba(var(--ink-rgb), 0.18);
			/* generous drop below the handle so content clears the absolute
			   ✕ button (its circle reaches ~58px from the sheet top) */
			margin: 0 auto 44px;
		}

		.m-actions {
			flex-direction: column-reverse;
			align-items: stretch;
			margin-top: 24px;
		}

		.m-actions > * {
			width: 100%;
		}

		/* the handle's bottom margin already clears the ✕ on sheets */
		.m-handle + button + .m-body {
			margin-top: 0;
		}
	}
`;

export const ModalCloseBtn = styled.button`
	position: absolute;
	top: 26px;
	right: 26px;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 38px;
	height: 38px;
	border-radius: 50%;
	border: 1.5px solid var(--hair);
	background: transparent;
	color: var(--ink);
	cursor: pointer;
	padding: 0;
	transition: border-color 0.15s;
	-webkit-tap-highlight-color: transparent;

	&:hover {
		border-color: var(--ink);
	}

	${SHEET_BREAK} {
		top: 14px;
		right: 16px;
	}
`;

/* Cookie consent switch rows inside the modal shell body. */
export const CookieOptions = styled.div`
	display: flex;
	flex-wrap: wrap;
	justify-content: center;
	gap: 10px 26px;
	margin-top: 20px;

	label {
		display: flex;
		align-items: center;
		gap: 10px;
		font-family: var(--fb);
		font-size: 15px;
		color: var(--ink);
	}

	${SHEET_BREAK} {
		flex-direction: column;
		align-items: flex-start;
	}
`;

/* Toast / snackbar stack (added-to-cart and friends). */
export const ToastViewport = styled.div`
	position: fixed;
	right: 24px;
	/* --toast-clearance is the height of a page's sticky bottom bar (KBar),
	   published on body by useToastClearance so toasts stack above it */
	bottom: calc(24px + var(--toast-clearance, 0px));
	z-index: 9800;
	display: flex;
	flex-direction: column;
	align-items: flex-end;
	gap: 10px;
	pointer-events: none;

	@media (max-width: 1079px) {
		left: 12px;
		right: 12px;
		bottom: calc(
			84px + env(safe-area-inset-bottom) + var(--toast-clearance, 0px)
		);
		align-items: stretch;
	}
`;

export type ToastSeverity = 'success' | 'error' | 'warning' | 'info';

// same severity mapping as the block alerts (Core.styles alertAccent);
// kept local so the style files don't import each other through the barrel
const toastAccent = (severity: ToastSeverity, mode: 'light' | 'dark') =>
	severity === 'error'
		? 'var(--coral)'
		: severity === 'warning'
			? mode === 'dark'
				? 'var(--gold)'
				: 'var(--gold-ink)'
			: severity === 'success'
				? 'var(--teal-dark)'
				: 'var(--mut)';

const toastTint = (severity: ToastSeverity) =>
	severity === 'error'
		? 'rgba(241, 107, 70, 0.16)'
		: severity === 'warning'
			? 'rgba(219, 177, 84, 0.22)'
			: severity === 'success'
				? 'rgba(0, 200, 150, 0.16)'
				: 'rgba(var(--ink-rgb), 0.07)';

export const ToastCard = styled.div<{
	$severity: ToastSeverity;
}>`
	pointer-events: auto;
	position: relative;
	display: flex;
	align-items: center;
	gap: 14px;
	min-width: 360px;
	max-width: 540px;
	padding: 8px 30px 8px 8px;
	background: var(--panel-2);
	color: var(--ink);
	border: 1px solid var(--hair);
	border-radius: var(--r-pill);
	box-shadow: ${({ theme }) =>
		theme.mode === 'dark'
			? '0 18px 40px rgba(0, 0, 0, 0.55)'
			: '0 18px 40px rgba(var(--ink-rgb), 0.2)'};
	font-family: var(--fb);
	/* the swipe is sideways, a vertical drag still scrolls the page */
	touch-action: pan-y;

	&.with-action {
		padding-right: 12px;
	}

	.t-ic {
		display: flex;
		align-items: center;
		justify-content: center;
		flex-shrink: 0;
		width: 50px;
		height: 50px;
		border-radius: 50%;
		background: ${({ $severity }) => toastTint($severity)};
		color: ${({ $severity, theme }) => toastAccent($severity, theme.mode)};

		svg {
			width: 21px;
			height: 21px;
		}
	}

	.t-msg {
		flex: 1;
		min-width: 0;
		line-height: 1.35;
	}

	.t-msg b {
		display: block;
		font-size: 16px;
		font-weight: 500;
	}

	.t-msg small {
		display: -webkit-box;
		margin-top: 1px;
		overflow: hidden;
		-webkit-box-orient: vertical;
		-webkit-line-clamp: 2;
		font-size: 14px;
		font-weight: 400;
		color: var(--mut);
	}

	.t-action {
		flex-shrink: 0;
		padding: 8px 15px;
		border: none;
		border-radius: var(--r-pill);
		background: none;
		box-shadow: inset 0 0 0 1.5px rgba(var(--ink-rgb), 0.28);
		color: var(--ink);
		cursor: pointer;
		font-family: var(--fd);
		font-size: 14.5px;
		letter-spacing: 0.3px;
		text-transform: lowercase;
		white-space: nowrap;
		transition: box-shadow 0.15s;

		&:hover {
			box-shadow: inset 0 0 0 1.5px var(--ink);
		}
	}

	.t-x {
		display: flex;
		flex-shrink: 0;
		margin: 0 -10px 0 -2px;
		padding: 4px;
		border: none;
		background: none;
		color: var(--mut);
		cursor: pointer;
		transition: color 0.15s;

		&:hover {
			color: var(--coral);
		}
	}

	&.with-action .t-x {
		margin: 0 3px 0 -4px;
	}

	/* touch screens swipe the toast away instead */
	@media (hover: none) {
		.t-x {
			display: none;
		}
	}

	/* the bigger sizes above are for desktop only */
	@media (max-width: 1079px) {
		gap: 12px;
		min-width: 0;
		max-width: none;
		width: 100%;
		padding: 7px 24px 7px 7px;

		&.with-action {
			padding-right: 9px;
		}

		.t-ic {
			width: 42px;
			height: 42px;

			svg {
				width: 18px;
				height: 18px;
			}
		}

		.t-msg b {
			font-size: 14.5px;
		}

		.t-msg small {
			font-size: 13px;
		}

		.t-action {
			padding: 6px 12px;
			font-size: 13px;
		}
	}
`;
